import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {
  Box,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import OfoqTextField from '../../../components/Forms/OfoqTextField.jsx'
import OfoqAutoComplete from '../../../components/Forms/OfoqAutoComplete.jsx'
import OfoqIosSwitch from '../../../components/Forms/OfoqIosSwitch.jsx'
import OfoqTextEditor from '../../../components/Forms/OfoqTextEditor.jsx'
import CardWithSpace from '../../../components/Cards/CardWithSpace/index.jsx'
import {
  addCompany,
  editCompany,
  lastTenant,
  retrieveCompaniesModules,
  retrieveCompaniesFabsTenants,
} from '../../../redux/features/companies/companiesThunk'
import { selectCompanies } from '../../../redux/features/companies/companiesSlice'
import { SAML_KEY } from '../../../utils/constants'
import { isNotEmpty } from '../../../utils/helpers'
import UseTranslation from '../../../hooks/UseTranslation.jsx'
import { ReactComponent as WarningTriangleIcon } from '../../../images/WarningTriangle.svg'
import { ReactComponent as DocumentLinedIcon } from '../../../images/DocumentLined.svg'

const INTEGRATION_TYPES = {
  STANDALONE: 'STANDALONE',
  OTMS_V2: 'OTMS_V2',
}

const INTEGRATION_TYPE_OPTIONS = [INTEGRATION_TYPES.STANDALONE, INTEGRATION_TYPES.OTMS_V2]
const REQUIRED_FIELDS_ERROR = 'Please fill in the required fields'
const NO_TENANT_ERROR = 'There are no available tenants. Please contact your system administrator'

const getModuleIds = (modules = []) => (modules || []).map((module) => module.id)
const hasCoreModuleSelected = (modules = []) => (
  modules?.some((module) => module?.id === 4 || module?.code === 'core') || false
)
const getCompanyPolicyValues = (company = {}) => {
  const companyPolicies = company?.company_policies || {}

  return {
    enabled: Boolean(companyPolicies?.enabled),
    message_ar: companyPolicies?.message_ar || '',
    message_en: companyPolicies?.message_en || '',
  }
}

const getCompanyPoliciesPayload = (state) => ({
  company_policies: state.companyPolicies,
})

const GeneralCompanyForm = forwardRef(({
  isEdit,
  initialRow,
  onSaved,
  onCanSubmitChange,
}, ref) => {
  const dispatch = useDispatch()
  const { t } = UseTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const companiesStore = useSelector(selectCompanies)

  const data = useMemo(() => {
    if (isEdit && initialRow) return initialRow
    return { tenant: companiesStore.lastTenant }
  }, [isEdit, initialRow, companiesStore.lastTenant])
  const initialCompanyPolicies = useMemo(
    () => getCompanyPolicyValues(data?.company),
    [data?.company],
  )

  const [companyName, setCompanyName] = useState(data?.company?.name)
  const [applicantUrl, setApplicantUrl] = useState(data?.company?.domain)
  const [integrationType, setIntegrationType] = useState(data?.integration?.type)
  const [companyModules, setCompanyModules] = useState(data?.company?.modules)
  const [coreTenant, setCoreTenants] = useState(data?.company?.tenant_list?.[0]?.fabs_tenant)
  const [tenantName, setTenantName] = useState(data?.tenant?.name || '')
  const [enableAuditing, setEnableAuditing] = useState(data?.company?.audit)
  const [enableSso, setEnableSso] = useState(false)
  const [ssoInitiallyEnabled, setSsoInitiallyEnabled] = useState(false)
  const [companyPolicies, setCompanyPolicies] = useState(initialCompanyPolicies)
  const [hasRequiredCompanyPolicyMessages, setHasRequiredCompanyPolicyMessages] = useState(false)
  const [consentEditorLang, setConsentEditorLang] = useState('en')

  const tenant = data?.tenant
  const company = data?.company

  const isOTMSV2 = integrationType === INTEGRATION_TYPES.OTMS_V2
  const isStandalone = integrationType === INTEGRATION_TYPES.STANDALONE
  const showApplicantPortalURL = companyModules?.find((item) => item?.code === 'recruitment')
  const isCoreModule = companyModules?.some((module) => module.code === 'core') || false

  const INTEGRATION_CONFIGS = useMemo(() => ({
    [INTEGRATION_TYPES.OTMS_V2]: {
      requiredFields: ['companyName', 'integrationType', 'companyModules', 'tenant', 'coreTenant'],
      customValidation: (state) => (
        !state.showApplicantPortalURL || isNotEmpty(state.applicantUrl)
      ),
      transform: (state) => ({
        name: state.companyName,
        domain: state.applicantUrl,
        integration_type: state.integrationType,
        module_ids: getModuleIds(state.companyModules),
        tenant_id: state.tenant.id,
        fabs_tenant_id: state.coreTenant?.id,
        audit: state.enableAuditing,
        ...getCompanyPoliciesPayload(state),
        ...(isNotEmpty(state.tenantName) ? { tenant_name: state.tenantName } : {}),
      }),
    },
    [INTEGRATION_TYPES.STANDALONE]: {
      requiredFields: ['companyName', 'integrationType', 'applicantUrl', 'tenant'],
      transform: (state) => ({
        name: state.companyName,
        domain: state.applicantUrl,
        integration_type: state.integrationType,
        tenant_id: state.tenant.id,
        ...getCompanyPoliciesPayload(state),
        ...(isNotEmpty(state.tenantName) ? { tenant_name: state.tenantName } : {}),
      }),
    },
  }), [])

  useEffect(() => {
    dispatch(retrieveCompaniesModules())
    if (!isEdit) {
      dispatch(lastTenant())
    }
  }, [dispatch, isEdit])

  useEffect(() => {
    if (isOTMSV2) {
      dispatch(retrieveCompaniesFabsTenants())
    }
  }, [dispatch, isOTMSV2])

  useEffect(() => {
    if (isEdit && initialRow) {
      const isSamlEnabled = initialRow?.company?.sso_protocol === SAML_KEY
      const companyPolicyValues = getCompanyPolicyValues(initialRow.company)
      setCompanyName(initialRow.company?.name)
      setApplicantUrl(initialRow.company?.domain)
      setIntegrationType(initialRow.integration?.type)
      setCompanyModules(initialRow.company?.modules)
      setCoreTenants(initialRow.company?.tenant_list?.[0]?.fabs_tenant)
      setTenantName(initialRow.tenant?.name || '')
      setEnableAuditing(initialRow.company?.audit)
      setEnableSso(isSamlEnabled)
      setSsoInitiallyEnabled(isSamlEnabled)
      setCompanyPolicies(companyPolicyValues)
    }
  }, [isEdit, initialRow?.company?.id])

  useEffect(() => {
    if (isEdit) return
    setTenantName(companiesStore.lastTenant?.name || '')
  }, [isEdit, companiesStore.lastTenant?.name])

  useEffect(() => {
    setHasRequiredCompanyPolicyMessages(
      String(companyPolicies?.message_en ?? '').trim().length > 0 &&
      String(companyPolicies?.message_ar ?? '').trim().length > 0,
    )
  }, [companyPolicies?.message_en, companyPolicies?.message_ar])

  const formData = useMemo(() => ({
    companyName,
    applicantUrl,
    integrationType,
    showApplicantPortalURL,
    companyModules,
    tenant,
    coreTenant,
    tenantName,
    enableAuditing,
    enableSso,
    companyPolicies,
  }), [
    companyName, applicantUrl, integrationType, showApplicantPortalURL, companyModules,
    tenant, coreTenant, tenantName, enableAuditing, enableSso,
    companyPolicies,
  ])

  const isConsentEnabled = Boolean(companyPolicies?.enabled)

  const storeHasCompanyPolicyData = useMemo(() => {
    if (!isEdit) return false
    const details = companiesStore?.companyDetails
    if (details?.id == null || String(details.id) !== String(company?.id)) return false
    const p = details?.company_policies
    if (p == null || typeof p !== 'object') return false
    return Boolean(
      p.enabled ||
      String(p.message_en || '').trim().length > 0 ||
      String(p.message_ar || '').trim().length > 0 ||
      (p.id != null && p.id !== '') ||
      (p.current_version != null && p.current_version !== ''),
    )
  }, [isEdit, companiesStore?.companyDetails, company?.id])

  const hasCompanyPolicyData = useMemo(() => {
    const draftEn = String(companyPolicies?.message_en ?? '').trim().length > 0
    const draftAr = String(companyPolicies?.message_ar ?? '').trim().length > 0
    return storeHasCompanyPolicyData || draftEn || draftAr
  }, [
    storeHasCompanyPolicyData,
    companyPolicies?.message_en,
    companyPolicies?.message_ar,
  ])

  const canSubmitGeneralStep = !isEdit || !isConsentEnabled || hasRequiredCompanyPolicyMessages

  const isValid = (config) => {
    if (!config) return false
    const hasBasicFields = config.requiredFields?.every((field) => isNotEmpty(formData[field]))
    const passesCustom = config.customValidation ? config.customValidation(formData) : true
    return hasBasicFields && passesCustom && canSubmitGeneralStep
  }

  useEffect(() => {
    onCanSubmitChange?.(canSubmitGeneralStep)
  }, [canSubmitGeneralStep, onCanSubmitChange])

  const runSave = useCallback((requestObj) => new Promise((resolve) => {
    const action = isEdit ? editCompany : addCompany
    const companyId = company?.id
    dispatch(action({
      body: requestObj,
      id: companyId,
    })).then((response) => {
      if (response?.meta?.requestStatus !== 'fulfilled') {
        resolve(false)
        return
      }
      enqueueSnackbar(
        isEdit
          ? `${companyName} ${t?.['has been edited successfully']}`
          : `${companyName} ${t?.['has been added successfully']}`,
        { variant: 'success' },
      )
      const savedPayload = response?.payload || {}
      const payloadCompany = (
        savedPayload?.response || savedPayload?.data || savedPayload?.company || savedPayload
      )
      const payloadCompanyId = payloadCompany?.id ?? savedPayload?.id
      const payloadCompanyName = payloadCompany?.name ?? savedPayload?.name
      const newId = isEdit ? companyId : payloadCompanyId
      onSaved?.({
        companyId: newId,
        mode: isEdit ? 'edit' : 'add',
        companyName: payloadCompanyName || companyName || requestObj?.name || '',
        hasCoreModule: hasCoreModuleSelected(companyModules),
      })
      resolve(true)
    })
  }), [
    dispatch, enqueueSnackbar, isEdit, company?.id, onSaved, t, companyName, companyModules,
  ])

  const submitGeneralStep = useCallback(async () => {
    const resolvedTenantId = isEdit ? tenant?.id : companiesStore.lastTenant?.id
    if (!isNotEmpty(resolvedTenantId)) {
      enqueueSnackbar(NO_TENANT_ERROR, { variant: 'error' })
      return false
    }
    const config = INTEGRATION_CONFIGS[integrationType]
    if (!isValid(config)) {
      enqueueSnackbar(REQUIRED_FIELDS_ERROR, { variant: 'error' })
      return false
    }
    const payload = {
      ...config.transform(formData),
      tenant_id: resolvedTenantId,
    }
    return runSave(payload)
  }, [
    isEdit,
    tenant,
    companiesStore.lastTenant?.id,
    INTEGRATION_CONFIGS,
    integrationType,
    formData,
    canSubmitGeneralStep,
    runSave,
    enqueueSnackbar,
  ])

  useImperativeHandle(ref, () => ({ submitGeneralStep }), [submitGeneralStep])

  const sectionHeader = (title, subtitle, badgeLabel) => (
    <Box sx={styles.sectionHeaderRow}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={styles.sectionTitle}>
          {title}
        </Typography>
        <Typography sx={styles.sectionSubtitle}>
          {subtitle}
        </Typography>
      </Box>
      {badgeLabel && (
        <Chip label={badgeLabel} size="small" sx={styles.badgeChip} />
      )}
    </Box>
  )

  const ssoPermanentLock = (
    isEdit && ssoInitiallyEnabled
  )
  const setCompanyPolicyField = (field) => (value) => {
    setCompanyPolicies((prev) => ({
      ...getCompanyPolicyValues(),
      ...(prev || {}),
      [field]: value,
    }))
  }

  if (isEdit && !initialRow) {
    return null
  }

  return (
    <Box sx={styles.root}>
      <CardWithSpace customStyles={styles.cardShell}>
        {sectionHeader(
          t?.['Company details'],
          t?.['Core identity and integration settings'],
          null,
        )}
        <Box sx={styles.sectionBody}>
          <Grid container rowSpacing={{ xs: 2, sm: 2.5 }} columnSpacing={{ xs: 1, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <OfoqTextField
                name={t?.['Company Name']}
                defaultValue={companyName}
                refState={setCompanyName}
                type="text"
                required
                customStyle={styles.field}
                customPlaceHolder={`${t?.['e.g.']} United OFOQ`}
              />
            </Grid>
            <Grid item xs={12} md={6} key={tenant?.id ? `tenant-${tenant.id}` : 'tenant-pending'}>
              <OfoqTextField
                defaultValue={tenantName}
                refState={setTenantName}
                placeholder={isEdit ? (tenant?.name || '') : (t?.['Auto-generated'])}
                name={t?.Tenant}
                disabled
                type="text"
                customStyle={styles.field}
              />
              <Typography sx={styles.helperText}>
                {isEdit
                  ? (t?.['Tenant ID cannot be changed after creation'])
                  : (t?.['Auto-generated from company name if left blank'])}
              </Typography>
            </Grid>
            <Grid item xs={12} md={isOTMSV2 ? 6 : 12} sx={isOTMSV2 ? {} : styles.singleFieldRow}>
              <OfoqAutoComplete
                name={t?.Integration}
                defaultOption={integrationType}
                refState={setIntegrationType}
                optionsConfig={INTEGRATION_TYPE_OPTIONS}
                customStyle={styles.autoCompleteInput}
                required
                nameConverter={(option) => option || ''}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>
            {isOTMSV2 && (
              <Grid item xs={12} md={6}>
                <OfoqAutoComplete
                  name={t?.['Core tenant']}
                  defaultOption={coreTenant}
                  refState={setCoreTenants}
                  optionsConfig={companiesStore.fabsTenants}
                  customStyle={styles.autoCompleteInput}
                  required
                />
              </Grid>
            )}
            {isStandalone && (
              <Grid item xs={12}>
                <OfoqTextField
                  name={t?.['Applicant portal URL']}
                  defaultValue={applicantUrl}
                  refState={setApplicantUrl}
                  type="url"
                  customPlaceHolder={t?.['Type the Applicant Portal URL']}
                  required
                  customStyle={styles.field}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </CardWithSpace>

      {isOTMSV2 && (
        <CardWithSpace customStyles={styles.cardShell}>
          {sectionHeader(
            t?.Modules,
            isEdit
              ? (t?.['Active product modules for this company'])
              : (t?.['Select active product modules for this company']),
            null,
          )}
          <Box sx={styles.sectionBody}>
            <Grid container rowSpacing={{ xs: 2, sm: 2.5 }} columnSpacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12}>
                <OfoqAutoComplete
                  name={t?.['Active Modules']}
                  defaultOption={companyModules}
                  refState={setCompanyModules}
                  optionsConfig={companiesStore.companyModules}
                  customStyle={{
                    ...styles.autoCompleteInput,
                    ...styles.modulesAutoCompleteInput,
                  }}
                  required
                  multiple
                  limitTags={5}
                />
              </Grid>
              {showApplicantPortalURL && (
                <Grid item xs={12}>
                  <OfoqTextField
                    name={t?.['Applicant portal URL']}
                    defaultValue={applicantUrl}
                    refState={setApplicantUrl}
                    type="url"
                    customPlaceHolder={t?.['Type the Applicant Portal URL']}
                    required
                    customStyle={styles.field}
                  />
                  <Typography sx={styles.helperText}>
                    {t?.['Visible because Recruitment module is active.']}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardWithSpace>
      )}

      {isOTMSV2 && (isCoreModule || ssoPermanentLock) && (
        <CardWithSpace customStyles={styles.cardShell}>
          {sectionHeader(
            t?.['Access & features'],
            t?.['Optional platform capabilities'],
            null,
          )}
          <Box sx={styles.sectionBody}>
            <Grid container spacing={2}>
              {isCoreModule && (
              <Grid item xs={12} md={ssoPermanentLock ? 6 : 12}>
                <Box sx={styles.toggleCardAuditing}>
                  <Box sx={styles.toggleHeaderRow}>
                    <Typography sx={styles.toggleTitle}>
                      {t?.['Enable Auditing']}
                    </Typography>
                    <OfoqIosSwitch
                      defaultValue={Boolean(enableAuditing)}
                      onChangeHandler={(e) => setEnableAuditing(e.target.checked)}
                      customStyle={styles.inlineSwitch}
                    />
                  </Box>
                  <Typography sx={styles.toggleDescription}>
                    {t?.['Track all user actions and system changes']}
                  </Typography>
                </Box>
              </Grid>
              )}
              {ssoPermanentLock && (
                <Grid item xs={12} md={ isCoreModule ? 6 : 12}>
                  <Box sx={styles.toggleCardSsoLocked}>
                    <Box sx={styles.toggleHeaderRow}>
                      <Box sx={styles.lockedSsoTitleWrap}>
                        <Typography sx={[styles.toggleTitle, styles.toggleTitleLocked]}>
                          {t?.['Enable SSO']}
                        </Typography>
                        <LockOutlinedIcon sx={styles.lockedSsoTitleIcon} />
                      </Box>
                      <OfoqIosSwitch
                        defaultValue={Boolean(enableSso)}
                        onChangeHandler={(e) => setEnableSso(e.target.checked)}
                        disabled
                        customStyle={styles.ssoSwitch}
                      />
                    </Box>
                    <Typography sx={[styles.toggleDescription, styles.toggleDescriptionLocked]}>
                      {t?.['Cannot be disabled once activated']}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            {ssoPermanentLock && (
              <Box sx={styles.ssoWarningBanner}>
                <Box sx={styles.ssoWarningIconWrap}>
                  <LockIcon sx={styles.ssoWarningIcon} />
                </Box>
                <Box>
                  <Typography sx={styles.ssoWarningTitle}>
                    {t?.['SSO is permanently active for this company']}
                  </Typography>
                  <Typography sx={styles.ssoWarningBody}>
                    {t?.['Once SSO is enabled it cannot be reverted. You can update the SSO configuration in step 2, but you cannot turn it off.']}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </CardWithSpace>
      )}

      {isEdit && (
        <CardWithSpace customStyles={styles.cardShell}>
          {sectionHeader(
            t?.['Consent Form'],
            t?.['Consent for below Data - every employee must read and approve before using the system'],
            null,
          )}
          <Box sx={styles.sectionBody}>
            <Box sx={styles.consentToggleCard}>
              <Box sx={styles.toggleHeaderRow}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={styles.toggleTitle}>
                    {t?.['Require employees to provide consent for below data']}
                  </Typography>
                  <Typography sx={styles.toggleDescription}>
                    {t?.['When enabled, every employee must read and approve the consent form on first sign-in']}
                  </Typography>
                </Box>
                <OfoqIosSwitch
                  defaultValue={Boolean(companyPolicies?.enabled)}
                  onChangeHandler={(e) => setCompanyPolicyField('enabled')(e.target.checked)}
                  customStyle={styles.inlineSwitch}
                />
              </Box>
            </Box>

            {isConsentEnabled && (
              <>
                <Box sx={styles.consentWarningBanner}>
                  <Box sx={styles.consentWarningIconWrap}>
                    <WarningTriangleIcon style={styles.consentWarningIcon} />
                  </Box>
                  <Box>
                    <Typography sx={styles.consentWarningTitle}>
                      {t?.['Editing this consent form creates a new version']}
                    </Typography>
                    <Typography sx={styles.consentWarningBody}>
                      <Box component="span">
                        {t?.['If you change either the English or Arabic text, a new consent form version is generated and ']}
                      </Box>
                      <Box component="span" sx={styles.consentWarningEmphasis}>
                        {t?.['all employees will be required to read and re-approve']}
                      </Box>
                      <Box component="span">
                        {t?.[' the consent form on their next sign-in. They will not be able to use the system until they approve.']}
                      </Box>
                    </Typography>
                  </Box>
                </Box>

                <Box sx={styles.consentTabsWrap}>
                  <Tabs
                    value={consentEditorLang}
                    onChange={(_, value) => setConsentEditorLang(value)}
                    variant="fullWidth"
                    sx={styles.consentTabs}
                  >
                    <Tab value="en" label={`\u2022 ${t?.English}`} sx={styles.consentTab} />
                    <Tab value="ar" label={`\u2022 ${t?.['العربية']}`} sx={styles.consentTab} />
                  </Tabs>
                </Box>

                <Box sx={styles.consentEditorWrap}>
                  {consentEditorLang === 'en' ? (
                    <OfoqTextEditor
                      key="company-policy-en"
                      name={t?.['Consent form text - English']}
                      defaultValue={companyPolicies?.message_en}
                      refState={setCompanyPolicyField('message_en')}
                      contentLanguage="en"
                      required
                      customStyle={styles.consentEditor}
                      maxCharacters={8000}
                    />
                  ) : (
                    <OfoqTextEditor
                      key="company-policy-ar"
                      name={t?.['Consent form text - Arabic']}
                      defaultValue={companyPolicies?.message_ar}
                      refState={setCompanyPolicyField('message_ar')}
                      contentLanguage="ar"
                      required
                      customStyle={styles.consentEditor}
                      maxCharacters={8000}
                    />
                  )}
                  <Typography sx={styles.consentHintText}>
                    {consentEditorLang === 'en'
                      ? (t?.['This version is displayed to users whose selected language is English.'])
                      : (t?.['This version is displayed to users whose selected language is Arabic.'])}
                  </Typography>
                </Box>
              </>
            )}

            {!isConsentEnabled && hasCompanyPolicyData && (
              <Box sx={styles.consentDisabledWarningBanner}>
                <Box sx={styles.consentDisabledIconWrap}>
                  <PriorityHighRoundedIcon sx={styles.consentDisabledIcon} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={styles.consentDisabledTitle}>
                    {t?.['Disabling the consent form']}
                  </Typography>
                  <Typography sx={styles.consentDisabledBody}>
                    <Box component="span">
                      {t?.['New employees will no longer be required to provide consent for personal data processing.']}
                    </Box>
                    <Box component="span" sx={styles.consentDisabledEmphasis}>
                      {t?.[' Existing consent records will be preserved']}
                    </Box>
                    <Box component="span">
                      {t?.[' for audit purposes. You can re-enable later - re-enabling will create a new consent form version that all employees must re-approve.']}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            )}

            {!isConsentEnabled && !hasCompanyPolicyData && (
              <Box sx={styles.consentEmptyState}>
                <Box sx={styles.consentEmptyStateIconWrap}>
                  <DocumentLinedIcon style={styles.consentEmptyStateSvg} />
                </Box>
                <Typography sx={styles.consentEmptyStateTitle}>
                  {t?.['No consent form required']}
                </Typography>
                <Typography sx={styles.consentEmptyStateBody}>
                  {t?.['Employees can use the system without providing consent for personal data processing. You can enable this later from company settings.']}
                </Typography>
              </Box>
            )}
          </Box>
        </CardWithSpace>
      )}
    </Box>
  )
})

GeneralCompanyForm.displayName = 'GeneralCompanyForm'

const styles = {
  root: {
    width: '100%',
    marginBottom: { xs: '50px', sm: 'unset' },
  },
  sectionHeaderRow: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'flex-start' },
    justifyContent: 'space-between',
    gap: 1.5,
    px: { xs: 2.5, sm: 3 },
    pt: { xs: 2.5, sm: 3 },
    pb: { xs: 2, sm: 2 },
    borderBottom: '1px solid #ebecef',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: { xs: '1rem', sm: '1.0625rem' },
    color: '#1b2559',
    lineHeight: 1.35,
  },
  sectionSubtitle: {
    mt: 0.75,
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    fontWeight: 400,
    color: '#92929d',
    lineHeight: 1.45,
    maxWidth: 560,
  },
  cardShell: {
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    boxShadow: 'none',
    marginBottom: '10px',
    overflow: 'hidden',
    padding: 0,
  },
  sectionBody: {
    px: { xs: 2.5, sm: 3 },
    pt: { xs: 2, sm: 2.5 },
    pb: { xs: 2.5, sm: 3 },
  },
  badgeChip: {
    alignSelf: { xs: 'flex-start', sm: 'center' },
    height: 26,
    fontWeight: 600,
    fontSize: '0.6875rem',
    color: '#29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid rgba(41, 204, 139, 0.45)',
    borderRadius: '999px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  helperText: {
    mt: 0.75,
    fontSize: '0.75rem',
    color: '#92929d',
    lineHeight: 1.45,
  },
  field: {
    mb: 0,
  },
  autoCompleteInput: {
    mb: '0px !important',
    '& .MuiAutocomplete-root': { mb: '0px !important' },
    '& .form-input': {
      height: { xs: 'auto', sm: 'unset' },
    },
  },
  modulesAutoCompleteInput: {
    '& .MuiOutlinedInput-root': {
      height: { xs: 'auto !important', sm: 'unset' },
      minHeight: { xs: '40px', sm: 'unset' },
      alignItems: 'flex-start',
      py: { xs: 0.5, sm: 0 },
      gap: { xs: '5px', sm: 'unset' },
    },
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'wrap',
    },
  },
  singleFieldRow: {
    '@media (min-width: 900px)': {
      '& .MuiFormControl-root, & .MuiFormGroup-root': {
        width: '100%',
      },
    },
  },
  toggleCardAuditing: {
    p: 2,
    borderRadius: '10px',
    border: '1px solid rgba(41, 204, 139, 0.35)',
    backgroundColor: '#fafbfc',
  },
  toggleCardSsoLocked: {
    p: 2,
    borderRadius: '10px',
    border: '1px solid rgba(229, 115, 115, 0.45)',
    backgroundColor: 'rgba(229, 115, 115, 0.06)',
  },
  toggleDescription: {
    mt: 1,
    fontSize: '0.8125rem',
    color: '#696974',
    lineHeight: 1.45,
  },
  toggleHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  lockedSsoTitleWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
  },
  lockedSsoTitleIcon: {
    color: '#b42318',
    fontSize: 18,
  },
  toggleTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1b2559',
  },
  toggleTitleLocked: {
    color: '#b42318',
  },
  inlineSwitch: {
    '&.MuiSwitch-root': {
      margin: 0,
    },
  },
  ssoSwitch: {
    '&.MuiSwitch-root': {
      margin: 0,
      backgroundColor: '#e57373',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#e57373 !important',
      opacity: 1,
    },
    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
      color: '#ffffff',
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#f2b8b5',
      opacity: 1,
    },
    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
      backgroundColor: '#f2b8b5',
      opacity: 1,
    },
    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': {
      backgroundColor: '#e57373',
      opacity: 1,
    },
    '& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb': {
      color: '#ffffff',
    },
  },
  toggleDescriptionLocked: {
    color: '#f04438',
  },
  ssoWarningBanner: {
    mt: 2,
    display: 'flex',
    gap: 1.5,
    p: 2,
    borderRadius: '10px',
    border: '1px solid rgba(229, 115, 115, 0.35)',
    backgroundColor: 'rgba(229, 115, 115, 0.08)',
    alignItems: 'flex-start',
  },
  ssoWarningIcon: {
    color: '#c62828',
    fontSize: 18,
    flexShrink: 0,
  },
  ssoWarningIconWrap: {
    width: 36,
    height: 36,
    borderRadius: '10px',
    border: '1px solid rgba(229, 115, 115, 0.5)',
    backgroundColor: 'rgba(229, 115, 115, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  ssoWarningTitle: {
    fontWeight: 700,
    fontSize: '0.9375rem',
    color: '#b71c1c',
    lineHeight: 1.35,
  },
  ssoWarningBody: {
    mt: 0.5,
    fontSize: '0.8125rem',
    color: '#6d1b1b',
    lineHeight: 1.5,
  },
  consentToggleCard: {
    p: 2,
    borderRadius: '10px',
    border: '1px solid rgba(41, 204, 139, 0.35)',
    backgroundColor: '#fafbfc',
  },
  consentWarningBanner: {
    mt: 2,
    display: 'flex',
    gap: 1.5,
    p: 2,
    borderRadius: '10px',
    border: '1px solid rgba(229, 115, 115, 0.35)',
    backgroundColor: 'rgba(229, 115, 115, 0.08)',
    alignItems: 'flex-start',
  },
  consentDisabledWarningBanner: {
    mt: 2,
    display: 'flex',
    gap: 1.5,
    p: 2,
    borderRadius: '12px',
    border: '1px solid #FCD34D',
    backgroundColor: '#FFFBEB',
    alignItems: 'flex-start',
  },
  consentDisabledIconWrap: {
    width: 22,
    height: 22,
    borderRadius: '999px',
    border: 'none',
    backgroundColor: '#d97706',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mt: 0.25,
  },
  consentDisabledIcon: {
    color: '#fff',
    fontSize: 14,
  },
  consentDisabledTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#92681A',
    lineHeight: 1.35,
  },
  consentDisabledBody: {
    mt: 0.5,
    fontSize: '0.9375rem',
    color: '#7C5811',
    lineHeight: 1.55,
  },
  consentDisabledEmphasis: {
    fontWeight: 700,
    color: '#7C5811',
  },
  consentEmptyState: {
    mt: 2,
    py: { xs: '32px', sm: '40px' },
    px: { xs: '20px', sm: '24px' },
    borderRadius: '14px',
    border: 'none',
    backgroundColor: '#F9FAFB',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  consentEmptyStateIconWrap: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mb: 2,
  },
  consentEmptyStateSvg: {
    width: 26,
    height: 26,
    color: '#9CA3AF',
    display: 'block',
  },
  consentEmptyStateTitle: {
    fontWeight: 600,
    fontSize: { xs: '0.9375rem', sm: '1rem' },
    color: '#374151',
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
    mb: 1.25,
    maxWidth: 440,
  },
  consentEmptyStateBody: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: '#B4B4B9',
    lineHeight: 1.5,
    maxWidth: 400,
  },
  consentWarningIconWrap: {
    width: 22,
    height: 22,
    borderRadius: '999px',
    border: '1px solid rgba(244, 67, 54, 0.35)',
    backgroundColor: '#f44336',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mt: 0.25,
  },
  consentWarningIcon: {
    width: 14,
    height: 14,
    color: '#fff',
  },
  consentWarningTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#b42318',
    lineHeight: 1.35,
  },
  consentWarningBody: {
    mt: 0.5,
    fontSize: '0.9375rem',
    color: '#b42318',
    lineHeight: 1.5,
  },
  consentWarningEmphasis: {
    fontWeight: 800,
  },
  consentTabsWrap: {
    mt: 2,
    borderBottom: '1px solid #ebecef',
    px: { xs: 0, sm: 1 },
  },
  consentTabs: {
    minHeight: 60,
    maxWidth: '100%',
    '& .MuiTabs-indicator': {
      backgroundColor: '#29CC8B',
      height: 2,
    },
  },
  consentTab: {
    textTransform: 'none',
    fontSize: { xs: '1rem', sm: '1.125rem' },
    fontWeight: 600,
    color: '#98a2b3',
    minHeight: 44,
    letterSpacing: 0,
    '&.Mui-selected': {
      color: '#19935f',
    },
  },
  consentEditorWrap: {
    mt: 2,
  },
  consentEditor: {
    mb: '0px !important',
    '& .ck.ck-editor__editable_inline': {
      minHeight: '220px !important',
      maxHeight: '220px',
      overflowY: 'auto',
      whiteSpace: 'pre-wrap',
    },
  },
  consentHintText: {
    mt: 1,
    fontSize: '0.8125rem',
    color: '#98a2b3',
    lineHeight: 1.45,
  },
}

export default GeneralCompanyForm
